/**
 * Smart Insights Engine — Rule-Based Financial Analysis
 * 
 * All analysis is performed locally using JavaScript math/logic.
 * No external AI API required.
 */

// ─── Helpers ────────────────────────────────────────────────

function fmtRp(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `Rp ${(value / 1_000_000_000).toFixed(1)} M`;
  if (abs >= 1_000_000) return `Rp ${(value / 1_000_000).toFixed(1)} Jt`;
  if (abs >= 1_000) return `Rp ${(value / 1_000).toFixed(1)} Rb`;
  return `Rp ${value.toLocaleString('id-ID')}`;
}

function pctChange(curr: number, prev: number): number {
  if (prev === 0) return curr === 0 ? 0 : 100;
  return ((curr - prev) / Math.abs(prev)) * 100;
}

function average(arr: number[]): number {
  if (arr.length === 0) return 0;
  return arr.reduce((s, v) => s + v, 0) / arr.length;
}

function stdDev(arr: number[]): number {
  const avg = average(arr);
  const sqDiffs = arr.map(v => (v - avg) ** 2);
  return Math.sqrt(average(sqDiffs));
}

export interface InsightItem {
  icon: string;       // emoji icon
  type: 'positive' | 'negative' | 'warning' | 'info' | 'neutral';
  text: string;
}

// ─── KPI Card Analysis ──────────────────────────────────────

export interface KPIAnalysisInput {
  title: string;
  value: number;
  prevValue?: number;
  kpis: {
    totalRevenue: number;
    cogs: number;
    grossProfit: number;
    opex: number;
    opProfit: number;
    netProfit: number;
    netCashFromOps: number;
    closingCash: number;
  };
  prevKpis?: {
    totalRevenue: number;
    cogs: number;
    grossProfit: number;
    opex: number;
    opProfit: number;
    netProfit: number;
    netCashFromOps: number;
    closingCash: number;
  } | null;
  /** Revenue breakdown data for composition analysis */
  revenueBreakdown?: {
    interestIncome: number;
    adminFee: number;
    gainLelang: number;
    lateFee: number;
    otherInterest: number;
  };
  /** Opex breakdown for composition */
  opexBreakdown?: {
    personnel: number;
    officeOps: number;
    depreciation: number;
    travel: number;
    otherOpex: number;
  };
}

export function analyzeKPI(input: KPIAnalysisInput): InsightItem[] {
  const { title, value, prevValue, kpis, revenueBreakdown, opexBreakdown } = input;
  const insights: InsightItem[] = [];
  const titleLower = title.toLowerCase();

  // ── Change vs previous period ──
  if (prevValue !== undefined && prevValue !== null) {
    const change = pctChange(value, prevValue);
    const diff = value - prevValue;
    const isExpenseType = titleLower.includes('opex') || titleLower.includes('expense');
    
    if (Math.abs(change) >= 0.5) {
      const direction = change > 0 ? 'naik' : 'turun';
      const goodForExpense = isExpenseType ? (change < 0) : (change > 0);
      
      insights.push({
        icon: goodForExpense ? '📈' : '📉',
        type: goodForExpense ? 'positive' : 'negative',
        text: `${direction.charAt(0).toUpperCase() + direction.slice(1)} ${Math.abs(change).toFixed(1)}% (${fmtRp(Math.abs(diff))}) vs periode sebelumnya`
      });

      // Significant change warning
      if (Math.abs(change) > 30) {
        insights.push({
          icon: '⚡',
          type: 'warning',
          text: `Perubahan signifikan (>${Math.abs(change).toFixed(0)}%) — perlu evaluasi lebih lanjut`
        });
      }
    } else {
      insights.push({
        icon: '➡️',
        type: 'neutral',
        text: `Relatif stabil dibanding periode sebelumnya`
      });
    }
  }

  // ── Revenue-specific insights ──
  if (titleLower.includes('revenue')) {
    // Gross margin
    if (kpis.totalRevenue > 0) {
      const grossMargin = (kpis.grossProfit / kpis.totalRevenue) * 100;
      insights.push({
        icon: '📊',
        type: grossMargin >= 70 ? 'positive' : grossMargin >= 50 ? 'info' : 'warning',
        text: `Margin kotor: ${grossMargin.toFixed(1)}% — ${grossMargin >= 70 ? 'Sangat sehat' : grossMargin >= 50 ? 'Cukup baik' : 'Perlu perhatian'}`
      });
    }
    
    // Revenue composition breakdown
    if (revenueBreakdown && kpis.totalRevenue > 0) {
      const components = [
        { name: 'Pendapatan Bunga', value: revenueBreakdown.interestIncome },
        { name: 'Admin Fee', value: revenueBreakdown.adminFee },
        { name: 'Gain Lelang', value: revenueBreakdown.gainLelang },
        { name: 'Denda Keterlambatan', value: revenueBreakdown.lateFee },
        { name: 'Lain-lain', value: revenueBreakdown.otherInterest },
      ].filter(c => c.value > 0).sort((a, b) => b.value - a.value);
      
      if (components.length > 0) {
        const top = components[0];
        const pct = (top.value / kpis.totalRevenue * 100).toFixed(0);
        insights.push({
          icon: '🏆',
          type: 'info',
          text: `Kontributor terbesar: ${top.name} (${pct}% dari total revenue)`
        });
      }
    }
  }

  // ── OpEx-specific insights ──
  if (titleLower.includes('opex') || titleLower.includes('expense')) {
    // OpEx to Revenue ratio
    if (kpis.totalRevenue > 0) {
      const ratio = (kpis.opex / kpis.totalRevenue) * 100;
      insights.push({
        icon: ratio > 100 ? '🔴' : ratio > 80 ? '🟡' : '🟢',
        type: ratio > 100 ? 'negative' : ratio > 80 ? 'warning' : 'positive',
        text: `Rasio OpEx/Revenue: ${ratio.toFixed(0)}% — ${ratio > 100 ? 'Pengeluaran melebihi pendapatan!' : ratio > 80 ? 'Mendekati batas wajar' : 'Dalam batas sehat'}`
      });
    }

    // OpEx composition
    if (opexBreakdown && kpis.opex > 0) {
      const components = [
        { name: 'Personil (Gaji)', value: opexBreakdown.personnel },
        { name: 'Operasional Kantor', value: opexBreakdown.officeOps },
        { name: 'Depresiasi', value: opexBreakdown.depreciation },
        { name: 'Perjalanan', value: opexBreakdown.travel },
        { name: 'Lainnya', value: opexBreakdown.otherOpex },
      ].filter(c => Math.abs(c.value) > 0).sort((a, b) => Math.abs(b.value) - Math.abs(a.value));

      if (components.length > 0) {
        const top = components[0];
        const pct = (Math.abs(top.value) / Math.abs(kpis.opex) * 100).toFixed(0);
        insights.push({
          icon: '💼',
          type: 'info',
          text: `Pengeluaran terbesar: ${top.name} (${pct}% dari total OpEx)`
        });
      }
    }
  }

  // ── Net Profit insights ──
  if (titleLower.includes('net profit') || titleLower.includes('laba bersih')) {
    if (value < 0) {
      insights.push({
        icon: '⚠️',
        type: 'negative',
        text: `Perusahaan mengalami KERUGIAN bersih pada periode ini`
      });
    } else if (value > 0) {
      insights.push({
        icon: '✅',
        type: 'positive',
        text: `Perusahaan PROFIT pada periode ini`
      });
    }

    // Net Profit Margin
    if (kpis.totalRevenue > 0) {
      const netMargin = (kpis.netProfit / kpis.totalRevenue) * 100;
      insights.push({
        icon: '📐',
        type: netMargin >= 10 ? 'positive' : netMargin >= 0 ? 'info' : 'negative',
        text: `Margin laba bersih: ${netMargin.toFixed(1)}%`
      });
    }

    // Operating leverage
    if (kpis.opProfit !== 0 && kpis.netProfit !== 0) {
      const opToNet = Math.abs(kpis.opProfit - kpis.netProfit);
      if (opToNet > Math.abs(kpis.netProfit) * 0.3) {
        insights.push({
          icon: '🔍',
          type: 'warning',
          text: `Selisih besar antara laba operasional dan laba bersih — cek pendapatan/beban lain-lain`
        });
      }
    }
  }

  // ── Petty Cash insights ──
  if (titleLower.includes('petty cash') || titleLower.includes('kas')) {
    if (value <= 0) {
      insights.push({
        icon: '🚨',
        type: 'negative',
        text: `Saldo kas NIHIL atau NEGATIF — sangat kritis!`
      });
    } else {
      insights.push({
        icon: '💵',
        type: 'info',
        text: `Saldo kas tersedia: ${fmtRp(value)}`
      });
    }
  }

  // ── AR/Piutang insights ──
  if (titleLower.includes('piutang') || titleLower.includes('ar')) {
    if (kpis.totalRevenue > 0) {
      const arToRev = (value / kpis.totalRevenue) * 100;
      insights.push({
        icon: arToRev > 80 ? '⚠️' : '📋',
        type: arToRev > 80 ? 'warning' : 'info',
        text: `Rasio Piutang/Revenue: ${arToRev.toFixed(0)}% — ${arToRev > 80 ? 'Banyak pendapatan belum tertagih' : 'Dalam batas wajar'}`
      });
    }
  }

  return insights;
}

// ─── Chart Analysis ─────────────────────────────────────────

export function analyzeChartTrend(data: number[], labels: string[], metricName: string): InsightItem[] {
  const insights: InsightItem[] = [];
  if (data.length < 2) return insights;

  // Average
  const avg = average(data);
  insights.push({
    icon: '📊',
    type: 'info',
    text: `Rata-rata ${metricName}/bulan: ${fmtRp(avg)}`
  });

  // Trend detection (last 3 data points)
  const recent = data.slice(-3);
  if (recent.length >= 3) {
    const allUp = recent[1] > recent[0] && recent[2] > recent[1];
    const allDown = recent[1] < recent[0] && recent[2] < recent[1];
    
    if (allUp) {
      insights.push({
        icon: '📈',
        type: 'positive',
        text: `Tren ${metricName} ↗ naik konsisten 3 bulan terakhir`
      });
    } else if (allDown) {
      insights.push({
        icon: '📉',
        type: 'negative',
        text: `Tren ${metricName} ↘ turun konsisten 3 bulan terakhir`
      });
    }
  }

  // Highest & lowest
  const maxVal = Math.max(...data);
  const minVal = Math.min(...data);
  const maxIdx = data.indexOf(maxVal);
  const minIdx = data.indexOf(minVal);
  
  if (labels[maxIdx] && labels[minIdx]) {
    insights.push({
      icon: '🔝',
      type: 'info',
      text: `Tertinggi: ${fmtRp(maxVal)} (${labels[maxIdx]}), Terendah: ${fmtRp(minVal)} (${labels[minIdx]})`
    });
  }

  // Anomaly detection using standard deviation
  const sd = stdDev(data);
  if (sd > 0) {
    data.forEach((val, i) => {
      const zScore = Math.abs(val - avg) / sd;
      if (zScore > 2 && labels[i]) {
        insights.push({
          icon: '⚡',
          type: 'warning',
          text: `Anomali terdeteksi di ${labels[i]}: ${fmtRp(val)} (${zScore > 2.5 ? 'sangat jauh' : 'jauh'} dari rata-rata)`
        });
      }
    });
  }

  return insights;
}

export function analyzeExpenseBreakdown(
  data: { category: string; value: number; color: string }[]
): InsightItem[] {
  const insights: InsightItem[] = [];
  if (data.length === 0) return insights;

  const total = data.reduce((s, d) => s + Math.abs(d.value), 0);
  if (total === 0) return insights;

  // Sort by value descending
  const sorted = [...data].sort((a, b) => Math.abs(b.value) - Math.abs(a.value));

  // Top contributor
  const top = sorted[0];
  const topPct = (Math.abs(top.value) / total * 100).toFixed(0);
  insights.push({
    icon: '🥇',
    type: 'info',
    text: `Pengeluaran terbesar: ${top.category} — ${topPct}% (${fmtRp(Math.abs(top.value))})`
  });

  // Second contributor if exists
  if (sorted.length >= 2) {
    const second = sorted[1];
    const secondPct = (Math.abs(second.value) / total * 100).toFixed(0);
    insights.push({
      icon: '🥈',
      type: 'info',
      text: `Kedua: ${second.category} — ${secondPct}% (${fmtRp(Math.abs(second.value))})`
    });
  }

  // Concentration warning
  if (Number(topPct) > 60) {
    insights.push({
      icon: '⚠️',
      type: 'warning',
      text: `Pengeluaran sangat terkonsentrasi pada ${top.category} (${topPct}%) — risiko tinggi jika kategori ini naik`
    });
  }

  return insights;
}

export function analyzeCashFlow(
  operating: number[],
  investing: number[],
  financing: number[],
  net: number[],
  labels: string[]
): InsightItem[] {
  const insights: InsightItem[] = [];
  if (operating.length === 0) return insights;

  const totalOps = operating.reduce((s, v) => s + v, 0);
  const totalInv = investing.reduce((s, v) => s + v, 0);
  const totalNet = net.reduce((s, v) => s + v, 0);

  // Operating cash flow health
  insights.push({
    icon: totalOps >= 0 ? '✅' : '⚠️',
    type: totalOps >= 0 ? 'positive' : 'negative',
    text: `Arus kas operasional: ${fmtRp(totalOps)} — ${totalOps >= 0 ? 'Operasi menghasilkan kas' : 'Operasi membakar kas!'}`
  });

  // Net cash position
  insights.push({
    icon: totalNet >= 0 ? '💰' : '📉',
    type: totalNet >= 0 ? 'positive' : 'warning',
    text: `Perubahan kas bersih: ${fmtRp(totalNet)} selama periode ini`
  });

  // Investing activity
  if (Math.abs(totalInv) > 0) {
    insights.push({
      icon: '🏗️',
      type: 'info',
      text: `Aktivitas investasi: ${fmtRp(totalInv)} — ${totalInv < 0 ? 'Ada pengeluaran modal (CapEx)' : 'Ada penerimaan dari investasi'}`
    });
  }

  // Count negative months
  const negativeMonths = net.filter(v => v < 0).length;
  if (negativeMonths > 0 && net.length > 1) {
    const pct = (negativeMonths / net.length * 100).toFixed(0);
    insights.push({
      icon: negativeMonths > net.length / 2 ? '🔴' : '🟡',
      type: negativeMonths > net.length / 2 ? 'warning' : 'info',
      text: `${negativeMonths} dari ${net.length} bulan memiliki arus kas negatif (${pct}%)`
    });
  }

  return insights;
}

export function analyzeNetProfitSeries(data: number[], labels: string[]): InsightItem[] {
  const insights: InsightItem[] = [];
  if (data.length === 0) return insights;

  // Count profitable vs loss months
  const profitable = data.filter(v => v > 0).length;
  const loss = data.filter(v => v < 0).length;

  insights.push({
    icon: profitable >= loss ? '📗' : '📕',
    type: profitable >= loss ? 'positive' : 'negative',
    text: `${profitable} bulan profit, ${loss} bulan rugi dari total ${data.length} bulan`
  });

  // Cumulative
  const cumulative = data.reduce((s, v) => s + v, 0);
  insights.push({
    icon: cumulative >= 0 ? '✅' : '❌',
    type: cumulative >= 0 ? 'positive' : 'negative',
    text: `Akumulasi laba/rugi: ${fmtRp(cumulative)} — ${cumulative >= 0 ? 'Total PROFIT' : 'Total RUGI'}`
  });

  // Trend detection
  if (data.length >= 3) {
    const recent = data.slice(-3);
    const allUp = recent[1] > recent[0] && recent[2] > recent[1];
    const allDown = recent[1] < recent[0] && recent[2] < recent[1];
    
    if (allUp) {
      insights.push({ icon: '🚀', type: 'positive', text: `Tren laba bersih membaik 3 bulan terakhir` });
    } else if (allDown) {
      insights.push({ icon: '⚠️', type: 'warning', text: `Tren laba bersih menurun 3 bulan terakhir` });
    }
  }

  // Best & worst month
  const maxVal = Math.max(...data);
  const minVal = Math.min(...data);
  const maxIdx = data.indexOf(maxVal);
  const minIdx = data.indexOf(minVal);

  if (labels[maxIdx]) {
    insights.push({
      icon: '🌟',
      type: 'info',
      text: `Bulan terbaik: ${labels[maxIdx]} (${fmtRp(maxVal)}), Bulan terburuk: ${labels[minIdx]} (${fmtRp(minVal)})`
    });
  }

  return insights;
}
